<template>
  <WidgetError v-if="loadError" @retry="retryInitialization" />
  <DetailPage v-else-if="initialized" heading="PUBLISHING.DETAIL.HEADING">
    <template #left-nav>
      <button class="link link-main icon-link" @click="back">
        <Icon icon="left" /> {{ $t('GENERAL.BACK') }}
      </button>
    </template>
    <template #right-nav>
      <a v-if="pubCLink" class="btn btn-light" :href="pubCLink" target="_blank">
        <Icon icon="outside" /> PubC
      </a>
      <a
        v-if="!hasMultipleDesigns"
        class="btn link icon-link download-icon"
        download
        target="_self"
        :href="designDownloadLink"
      >
        <Icon icon="savedisk" />
      </a>
      <button
        class="btn btn-light-danger delete-btn"
        @click="triggerDeleteComposition"
      >
        <Icon icon="bin" />
      </button>
      <VDropdown>
        <button href="#" class="btn btn-light info-btn" @click.prevent>
          <Icon icon="info-circle" />
        </button>
        <template #popper>
          <CompositionInfos :composition="composition" />
        </template>
      </VDropdown>

      <ProgressButton
        class="btn btn-primary save-btn"
        :on-click="save"
        :label="saveLabel"
        :disabled="!dirty"
      />
    </template>
    <template #content>
      <div>
        <div class="publishing-detail-content">
          <div class="configuration-menu">
            <div class="configuration-menu-content">
              <PublishingConfigMenuItem
                v-if="displayShop"
                :target-ref="refsReady ? $refs.publishShop : null"
                :disabled="rejected"
              >
                {{ $t('PUBLISHING.DETAIL.SHOP.HEADING') }}
              </PublishingConfigMenuItem>
              <PublishingConfigMenuItem
                v-if="displayMarketplace"
                :target-ref="refsReady ? $refs.publishMp : null"
                :disabled="rejected"
              >
                {{ $t('PUBLISHING.DETAIL.MP.HEADING') }}
              </PublishingConfigMenuItem>
              <PublishingConfigMenuItem
                :target-ref="refsReady ? $refs.details : null"
                :error="$refs.metaData && $refs.metaData.hasError"
                :disabled="rejected"
              >
                {{ $t('PUBLISHING.DETAIL.META_DATA.HEADING') }}
              </PublishingConfigMenuItem>
              <PublishingConfigMenuItem
                :target-ref="refsReady ? $refs.products : null"
                :disabled="rejected"
              >
                {{ $t('PUBLISHING.DETAIL.PRODUCTS.HEADING') }}
              </PublishingConfigMenuItem>
            </div>
          </div>
          <div class="configuration">
            <Warning
              v-if="rejected"
              class="hint"
              :heading="`${$t('PUBLISHING.DETAIL.REJECTED_HINT')}: ${$t(rejectionReasonName)}`"
              subheading="PUBLISHING.STATUS.REJECTED.DESCRIPTION"
              :faq-link="rejectedFaq"
              :variant="WarningVariant.ERROR"
            />
            <Warning
              v-if="publicWithNoSellables"
              class="hint"
              :heading="`${$t('PUBLISHING.DETAIL.NO_SELLABLES_HINT.HEADING')}`"
              subheading="PUBLISHING.DETAIL.NO_SELLABLES_HINT.TEXT"
              :variant="WarningVariant.INFO"
            />
            <div>
              <ConfigSection
                heading="PUBLISHING.DETAIL.PRODUCTS.HEADING"
                subheading="PUBLISHING.DETAIL.PRODUCTS.SUBHEADING"
                :disabled="rejected"
              >
                <ProductSettingsModeSwitcher />
                <PublishingProducts />
              </ConfigSection>
            </div>
            <div>
              <div ref="products" class="scroll-target"></div>

              <ConfigSection
                heading="PUBLISHING.DETAIL.META_DATA.HEADING"
                subheading="PUBLISHING.DETAIL.META_DATA.SUBHEADING"
                :disabled="rejected"
              >
                <InlineOnboarding
                  text="PUBLISHING.DETAIL.META_DATA.ONBOARDING"
                  :condensed="hasIntentForCurrentScope"
                  :condensed-style="inlineOnboardingCondensedStyle"
                  class="onboarding-container"
                />
                <PublishingMetaData
                  ref="metaData"
                  :validate="strictValidation && shouldValidate"
                />
              </ConfigSection>
              <div ref="details" class="scroll-target"></div>
            </div>
            <div v-if="displayMarketplace">
              <ConfigSection
                heading="PUBLISHING.DETAIL.MP.HEADING"
                subheading="PUBLISHING.DETAIL.MP.SUBHEADING"
                :disabled="rejected"
              >
                <InlineOnboarding
                  text="PUBLISHING.DETAIL.MP.ONBOARDING"
                  :condensed="hasIntentForCurrentScope"
                  :condensed-style="inlineOnboardingCondensedStyle"
                  class="onboarding-container"
                />
                <PublishingMpConfig />
              </ConfigSection>
            </div>
            <div ref="publishMp" class="scroll-target"></div>
            <div v-if="displayShop">
              <ConfigSection
                heading="PUBLISHING.DETAIL.SHOP.HEADING"
                subheading="PUBLISHING.DETAIL.SHOP.SUBHEADING"
                :disabled="rejected"
              >
                <InlineOnboarding
                  text="PUBLISHING.DETAIL.SHOP.ONBOARDING"
                  :condensed="hasIntentForCurrentScope"
                  :condensed-style="inlineOnboardingCondensedStyle"
                  class="onboarding-container"
                />
                <PublishingShopConfig />
              </ConfigSection>
            </div>
            <div ref="publishShop" class="scroll-target"></div>
          </div>
        </div>
      </div>
    </template>
  </DetailPage>
  <div v-else class="loader">
    <LoadingHeart />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  mapState as mapPiniaState,
  mapActions as mapPiniaActions,
} from 'pinia';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import ConfigSection from 'src/app/components/publishing/ConfigSection.vue';
import PublishingMpConfig from './PublishingMpConfig.vue';
import PublishingShopConfig from './PublishingShopConfig.vue';
import PublishingProducts from './PublishingProducts.vue';
import PublishingMetaData from './PublishingMetaData.vue';
import PublishingConfigMenuItem from './PublishingConfigMenuItem.vue';
import CompositionInfos from './CompositionInfos.vue';
import ConfirmDialog from 'commons/dialogs/confirm/ConfirmDialog.vue';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import unsavedChangesService from 'commons/unsavedChangesService/unsavedChangesService';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';
import InlineOnboarding from 'src/app/components/onboarding/InlineOnboarding.vue';
import PdvDialog from 'commons/dialogs/address/PdvDialog.vue';
import { usePublishingStore } from 'commons/stores/publishing';
import analytics from 'commons/tracking/analytics';
import Warning, {
  WarningVariant,
} from 'src/app/components/warnings/Warning.vue';
import smartFaqLinks from 'commons/smartFaq/smartFaqLinks';
import {
  getCompositionPosStateForPosType,
  COMPOSITION_POS_TYPES,
  COMPOSITION_POS_STATES,
} from 'commons/api/models/publishingCore/compositionPosStates';
import { rejectionReasonName } from 'commons/api/models/publishingCore/publishingCore';
import compositionHelper from 'commons/composition/compositionHelper';
import { useCompositionStore } from 'commons/stores/composition';
import { useCompositionsStore } from 'commons/stores/compositions';
import ProductSettingsModeSwitcher from './productSettings/ProductSettingsModeSwitcher.vue';
import WidgetError from 'src/app/components/error/WidgetError.vue';

export default {
  name: 'PublishingDetail',
  components: {
    LoadingHeart,
    WidgetError,
    ProgressButton,
    ConfigSection,
    PublishingMpConfig,
    PublishingShopConfig,
    PublishingProducts,
    PublishingMetaData,
    PublishingConfigMenuItem,
    CompositionInfos,
    DetailPage,
    InlineOnboarding,
    Warning,
    ProductSettingsModeSwitcher,
  },
  data() {
    return {
      initialized: false,
      loadError: false,
      displayMenu: true,
      refsReady: false,
      strictValidation: undefined,
      inlineOnboardingCondensedStyle: {
        'margin-top': '-16px',
      },
      rejectedFaq: smartFaqLinks.REJECTION_HELP,
      WarningVariant,
    };
  },
  computed: {
    ...mapPiniaState(usePublishingStore, [
      'isShopScope',
      'isMpScope',
      'currentScope',
    ]),
    ...mapPiniaState(useCompositionStore, {
      composition: 'current',
      compositionMode: (store) => store.mode,
      dirty: (store) => store.dirty,
      isPublic: (store) => store.isPublic,
      shouldValidate: (store) => store.shouldValidate,
      designDownloadLink: (store) => store.designDownloadLink,
    }),
    ...mapGetters({
      isAdmin: 'user/isAdmin',
      hasMarketplace: 'user/hasMarketplace',
      hasRegisteredShops: 'publishingCore/hasRegisteredShops',
      hasIntentForCurrentScope: 'publishingCore/hasIntentForCurrentScope',
      hasPublishRequirements: 'user/hasPublishRequirements',
    }),
    displayMarketplace() {
      return this.isMpScope && this.hasMarketplace;
    },
    displayShop() {
      return this.isShopScope && this.hasRegisteredShops;
    },
    saveLabel() {
      return this.isPublic && this.composition.sellables.length
        ? 'GENERAL.SAVE_AND_PUBLISH'
        : 'GENERAL.SAVE';
    },
    pubCLink() {
      if (!this.isAdmin) {
        return null;
      }

      return `https://pubc-insight-${
        SPRD.PLATFORM === 'NA' ? 'na' : 'eu'
      }.sprd.net/compositions?compositionId=${this.composition.id}`;
    },
    publicWithNoSellables() {
      return this.composition.sellables.length === 0 && this.isPublic;
    },
    rejected() {
      return (
        getCompositionPosStateForPosType({
          composition: this.composition,
          posType: this.displayMarketplace
            ? COMPOSITION_POS_TYPES.MP
            : COMPOSITION_POS_TYPES.SHOP,
        })?.status === COMPOSITION_POS_STATES.REJECTED
      );
    },
    rejectionReasonName() {
      return rejectionReasonName(
        this.displayMarketplace
          ? this.composition.publishingDetails.d2c.mpAccreditation
              ?.rejectionReasons?.[0]
          : this.composition.publishingDetails.spreadShops.accreditation
              ?.rejectionReasons?.[0],
      );
    },
    hasMultipleDesigns() {
      return compositionHelper.hasMultipleDesigns(this.composition);
    },
  },
  async created() {
    // Triggers the load itself rather than waiting on one the login init may
    // never have started, and always settles, so a direct reload of a detail
    // URL renders instead of hanging forever (DEV-231297).
    await this.$store.dispatch('publishingCore/ensureDataLoaded');
    await this.initialize();

    setTimeout(() => {
      this.refsReady = true;
    }, 200);

    unsavedChangesService.registerComponent({
      check: () => this.dirty,
      save: () => {
        return this.save();
      },
      discard: () => {
        this.resetToOriginal();
      },
    });
  },
  methods: {
    async initialize() {
      // The load always settles now, so the page renders an error state with a
      // retry instead of staying blank (DEV-231297).
      if (this.$store.state.publishingCore.loadError) {
        this.loadError = true;
        return;
      }

      this.loadError = false;

      try {
        await this.fetchComposition(this.$route.params.compositionId);
        this.ensureCompositionScope();
        this.initialized = true;
      } catch (error) {
        console.error('publishing detail initialization failed', error);
        this.loadError = true;
      }
    },
    async retryInitialization() {
      this.loadError = false;

      if (this.$store.state.publishingCore.loadError) {
        await this.$store.dispatch('publishingCore/fetchData');
      }

      await this.initialize();
    },
    ...mapPiniaActions(useCompositionStore, [
      'fetchComposition',
      'updateComposition',
      'resetToOriginal',
    ]),
    ...mapPiniaActions(useCompositionsStore, ['deleteComposition']),
    async triggerDeleteComposition() {
      try {
        await dialogService.openDialog(ConfirmDialog, {
          heading: 'DESIGNS.DELETE.SINGLE_WARNING',
          cancel: 'GENERAL.CANCEL',
          confirm: 'GENERAL.DELETE',
          danger: true,
        });
        await this.deleteComposition(this.composition.id);
        // Unsaved edits died with the composition; otherwise leaving asks to save them.
        this.resetToOriginal();
        this.$router.push({
          name: this.currentScope.routes.listPage,
          query: { restoreState: true },
        });
      } catch (_) {
        // nothing
      }
    },
    back() {
      this.$router.push({
        name: this.currentScope.routes.listPage,
        query: { restoreState: true },
      });
    },
    async save() {
      const hasIntentBeforeSaving = this.hasIntentForCurrentScope;

      if (this.isPublic && !this.hasPublishRequirements) {
        try {
          await dialogService.openDialog(PdvDialog);
        } catch (error) {
          return Promise.reject(error);
        }
      }

      this.strictValidation = true;
      await this.updateComposition();
      this.strictValidation = undefined;

      if (this.isPublic) {
        analytics.logEvent('composition_save_and_publish_success');
      } else {
        analytics.logEvent('composition_save_success');
      }

      // trigger hotjar survey
      if (hasIntentBeforeSaving && typeof window?.hj === 'function') {
        window.hj('event', 'display_publishing_survey');
      }
    },
    ensureCompositionScope() {
      if (!this.composition?.scopes?.includes(this.currentScope?.key)) {
        this.$router.push({
          name: this.currentScope.routes.listPage,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/type';

.download-icon {
  border-color: $pa-color-green;
  .icon {
    width: 14px;
    height: 14px;
    color: $pa-color-green;
    margin-right: 0;
  }
}

.publishing-detail-content {
  display: flex;
  gap: 80px;
  padding: 0 24px;
}

.configuration-menu {
  .configuration-menu-content {
    position: sticky;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column-reverse;
    padding: 24px 0 0 0;
  }
  a {
    margin: 0 0 16px 0;

    &.active {
      font-weight: bold;
    }
  }
}

.configuration {
  flex: 1;
  padding: 24px 0 0 0;

  & > div {
    &.scroll-target {
      margin: 0;
      padding: 0;
      visibility: hidden;
    }
  }

  .spacer {
    height: 24px;
  }
}

.onboarding-container {
  margin-bottom: 24px;
}

.hint {
  margin-bottom: 24px;
}
</style>

<style lang="scss">
#application.state-partnerarea-mp-publishingDetail,
#application.state-partnerarea-shop-publishingDetail {
  overflow-y: initial;

  .navbar-main {
    display: none;
  }

  #main-content {
    overflow: initial;
    margin-left: 0;
  }
}
</style>
